import React, { useEffect, useMemo } from "react";

import { Col, Form, Row } from "react-bootstrap";

import classNames from "classnames";
import { Controller, FieldArrayWithId, useFormContext } from "react-hook-form";

import { Participant, ParticipantTypes, RecipientRelationshipInput } from "@api/graphql";
import { ParticipantSearchField } from "@components/ParticipantSearchField";
import { FormErrorText } from "@components/form/FormErrorText";
import { formatDateStringLong, formatPercentage } from "@util/index";

import styles from "./AddBeneficiaryModal.module.scss";

export enum BeneficiaryType {
  Existing = "existing",
  Shell = "shell",
}

export const StartDateComponent = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<{ start_date: string; relationships: RecipientRelationshipInput[] }>();

  return (
    <Row className="mb-4">
      <Col md={6}>
        <Form.Group controlId="start_date">
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            placeholder="Enter start date"
            {...register("start_date", { required: "Start date is required" })}
            defaultValue={formatDateStringLong(new Date().toISOString())}
          />
          {errors.start_date && <FormErrorText error={errors.start_date} />}
        </Form.Group>
      </Col>
    </Row>
  );
};

interface ParticipantFormPropType {
  index: number;
  handleParticipantChange(index: number, selectedParticipant: Participant | undefined): void;
  selectedParticipants: {
    [key: number]: Participant;
  };
  selectedParticipantIds: Set<number>;
  currentParticipantTypes?: ParticipantTypes[];
}

export type ExtendedRecipientRelationshipInput = RecipientRelationshipInput & {
  beneficiary_type?: string;
  shell_beneficiary_name?: string;
};

export const ParticipantSearchForm = ({
  index,
  handleParticipantChange,
  selectedParticipants,
  selectedParticipantIds,
  currentParticipantTypes,
}: ParticipantFormPropType) => {
  const isCollaborationAccount = currentParticipantTypes?.includes(ParticipantTypes.COLLABORATION);
  const {
    control,
    register,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<{
    start_date: string;
    relationships: ExtendedRecipientRelationshipInput[];
  }>();
  const beneficiaryType = watch(`relationships.${index}.beneficiary_type`);
  const shellBeneficiaryName = watch(`relationships.${index}.shell_beneficiary_name`);
  const allShellNames = useMemo(() => {
    return watch("relationships") // Get all shell beneficiary names
      ?.filter((rel) => rel.beneficiary_type === "shell")
      .map((rel) => rel.shell_beneficiary_name);
  }, [watch]);

  // Get all selected participants' full names
  const selectedParticipantNames = useMemo(() => {
    return Object.values(selectedParticipants).map((p) => p.full_name);
  }, [selectedParticipants]);

  useEffect(() => {
    if (beneficiaryType === BeneficiaryType.Shell && shellBeneficiaryName) {
      const isDuplicateShell = allShellNames.filter((name) => name === shellBeneficiaryName).length > 1;
      const isDuplicateWithParticipant = selectedParticipantNames.includes(shellBeneficiaryName);

      if (isDuplicateShell) {
        setError(`relationships.${index}.shell_beneficiary_name`, {
          type: "manual",
          message: "Shell Beneficiary Name must be unique",
        });
      } else if (isDuplicateWithParticipant) {
        setError(`relationships.${index}.shell_beneficiary_name`, {
          type: "manual",
          message: "Shell Beneficiary Name cannot match an existing participant",
        });
      } else {
        clearErrors(`relationships.${index}.shell_beneficiary_name`);
      }
    }
  }, [shellBeneficiaryName, allShellNames, selectedParticipantNames, index, setError, clearErrors, beneficiaryType]);

  return (
    <>
      <Col md={3}>
        <Form.Group controlId={`beneficiary_type_${index}`}>
          <Form.Label>Beneficiary Type</Form.Label>
          <Form.Control as="select" {...register(`relationships.${index}.beneficiary_type` as const)}>
            <option value={BeneficiaryType.Existing}>{BeneficiaryType.Existing}</option>
            {!isCollaborationAccount && <option value={BeneficiaryType.Shell}>{BeneficiaryType.Shell}</option>}
          </Form.Control>
        </Form.Group>
      </Col>
      <Col md={4}>
        {beneficiaryType === "existing" && (
          <Controller
            name={`relationships.${index}.recipient_id` as const}
            control={control}
            rules={{
              required: "Recipient is required",
              validate: (value) => value !== 0 || "Recipient is required",
            }}
            render={({ field, fieldState }) => (
              <>
                <ParticipantSearchField
                  id={`async-typeahead-${index}`}
                  label="Beneficiary"
                  placeholder="Type to search..."
                  labelKey="full_name"
                  onChange={(selected: Participant | undefined) => {
                    field.onChange(selected);
                    handleParticipantChange(index, selected);
                  }}
                  selected={selectedParticipants[index] ? [selectedParticipants[index]] : []}
                  selectedParticipantIds={selectedParticipantIds}
                  currentParticipantTypes={currentParticipantTypes}
                  allowDeceased={isCollaborationAccount ? true : false}
                />
                <FormErrorText error={fieldState.error} />
              </>
            )}
          />
        )}
        {beneficiaryType === BeneficiaryType.Shell && (
          <>
            <Form.Label>Shell Beneficiary Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Shell Beneficiary Name"
              {...register(`relationships.${index}.shell_beneficiary_name`, {
                required: "Shell Beneficiary Name is required",
              })}
            />
            {errors?.relationships?.[index]?.shell_beneficiary_name && (
              <FormErrorText error={errors.relationships[index].shell_beneficiary_name} />
            )}
          </>
        )}
      </Col>
    </>
  );
};

interface PercentageFormPropType {
  item: FieldArrayWithId<
    {
      start_date: string;
      relationships: RecipientRelationshipInput[];
    },
    "relationships",
    "_id"
  >;
  index: number;
  recipient_percentage_share: number;
  conferrer_share: number;
}

export const PercentageShareForm = ({
  item,
  index,
  recipient_percentage_share,
  conferrer_share,
}: PercentageFormPropType) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<{ start_date: string; relationships: RecipientRelationshipInput[] }>();

  return (
    <>
      <Col md={2}>
        <Form.Group controlId={`percentage_share_${item._id}`}>
          <Form.Label>Percentage</Form.Label>
          <Form.Control
            {...register(`relationships.${index}.percentage_share` as const, {
              required: true,
              valueAsNumber: true,
              validate: (value) => value > 0 || "Percentage share must be greater than zero",
            })}
            type="number"
            placeholder="Enter share"
            step="0.01"
          />
          <FormErrorText error={errors.relationships?.[index]?.percentage_share} />
        </Form.Group>
      </Col>

      <Col md={2}>
        <Form.Group controlId={`calculated_percentage_share_${item._id}`}>
          <Form.Label>Calculated %</Form.Label>
          <Form.Control
            value={formatPercentage((recipient_percentage_share * conferrer_share) / 100)}
            type="number"
            disabled
          />
        </Form.Group>
      </Col>
    </>
  );
};

interface TotalPercentageProps {
  totalPercentage: number;
}

export const TotalPercentageDisplay = ({ totalPercentage }: TotalPercentageProps) => {
  return (
    <Row className="mt-4 mb-4">
      <Col className={styles["total-percentage"]}>
        <span>Total percentage</span>
        <span className={classNames(styles["total-percentage-value"], "b2")}>{formatPercentage(totalPercentage)}%</span>
      </Col>
    </Row>
  );
};
